import { Attr, BelongsTo, HasMany, HasOne, Model } from "spraypaint";

import { type StreamOptionsFilters } from "shared/helpers/StreamFilters";
import {
  ApplicationRecord,
  objectDirtyChecker,
} from "shared/services/spraypaint";
import type { Nullable } from "shared/types";
import {
  type MentionType,
  type SocialMentionType,
} from "shared/types/mentions";

import type StreamFilter from "./StreamFilter";
import type StreamGroup from "./StreamGroup";
import type StreamNotification from "./StreamNotification";
import type StreamSource from "./StreamSource";
import type StreamsUser from "./StreamsUser";
import type User from "./User";

export enum CaldSource {
  disabled = "disabled",
  enabled = "enabled",
  exclusively = "exclusively",
}

export enum SimpleBooleanFilterType {
  AT_LEAST = "at_least",
  AUTHOR = "author",
  CASE_SENSITIVE = "case_sensitive",
  FIRST = "first",
  MEDIUM = "medium",
  PLATFORM = "platform",
  QUERY_SNIPPETS = "query_snippets",
  TITLE = "title",
}

export type SimpleBooleanFilter = {
  isSuggestion?: boolean;
  keyword?: string;
  suggestionIndex?: Nullable<number>;
  type?: SimpleBooleanFilterType;
  value?: string | string[] | number;
};

export interface SimpleBoolean {
  must: SimpleBooleanFilter[];
  must_not?: SimpleBooleanFilter[];
  should?: SimpleBooleanFilter[];
}

@Model()
class Stream extends ApplicationRecord {
  static jsonapiType = "streams";

  @Attr({ persist: false }) activeUsersCount: number;

  @Attr({ persist: false }) archivedAt: Nullable<string>;

  @Attr({ persist: false }) bookmarksCount: number;

  @Attr() booleanQuery: string;

  @Attr() caseSensitive: boolean;

  @Attr() color: Nullable<string>;

  @Attr() dashboardEnabled: boolean;

  @Attr() excludedKeywords: Nullable<string>;

  @Attr() expiresAt: Nullable<string>;

  @HasMany() filters: StreamFilter[];

  @Attr() filterSetIds: number[];

  @HasOne() group: StreamGroup;

  @Attr() groupId: number;

  @Attr() groupLabel: string;

  @Attr() includeOnlineCaldSources: CaldSource;

  @Attr() includePrintCaldSources: CaldSource;

  @Attr() internationalMax: boolean;

  @Attr() internationalPlus: boolean;

  @Attr() invalidQuery: boolean;

  @Attr({ persist: false }) isBrief: boolean;

  @Attr() keywords: Nullable<string>;

  @Attr() label: string;

  @Attr() linkedStreamId: number;

  @Attr() magazineContent: boolean;

  @Attr() magazineTier: boolean;

  @Attr() matching: string;

  @Attr({ persist: false }) notificationsCounts: number;

  // content
  @Attr() onlineContent: boolean;

  // filters
  @Attr() onlineTier: boolean;

  @Attr() options: Nullable<StreamOptionsFilters>;

  @Attr() organisationId: number;

  @Attr() organisationTeamId: number;

  @Attr() podcastContent: boolean;

  @Attr() podcastTier: boolean;

  @Attr() printContent: boolean;

  @Attr() printTier: boolean;

  @Attr() queryStyle: "boolean" | "keywords" | "simple_boolean";

  @Attr() radioContent: boolean;

  @Attr() radioTier: boolean;

  @Attr() reportEnabled: boolean;

  @Attr({ persist: false }) rssUrl: string;

  // extra
  @Attr({ persist: false }) scheduledReportSpecificationsCount: number;

  @Attr({ dirtyChecker: objectDirtyChecker })
  selectedMagazineContent: MentionType[];

  @Attr({ dirtyChecker: objectDirtyChecker })
  selectedOnlineContent: MentionType[];

  @Attr({ dirtyChecker: objectDirtyChecker })
  selectedPodcastContent: MentionType[];

  @Attr({ dirtyChecker: objectDirtyChecker })
  selectedPrintContent: MentionType[];

  @Attr({ dirtyChecker: objectDirtyChecker })
  selectedRadioContent: MentionType[];

  @Attr({ dirtyChecker: objectDirtyChecker })
  selectedSocialContent: SocialMentionType[];

  @Attr({ dirtyChecker: objectDirtyChecker }) selectedTvContent: MentionType[];

  @Attr() sentimentModeEnabled: boolean;

  @Attr() simpleBoolean: Nullable<SimpleBoolean>;

  @Attr({ persist: false }) slug: string;

  @Attr() socialBookmarksStream: boolean;

  @Attr() socialContent: boolean;

  @Attr() socialImpactThreshold: number;

  @Attr() socialOnlyVerified: boolean;

  @Attr() socialTier: boolean;

  @HasMany() sources: StreamSource[];

  @HasMany() streamNotifications: StreamNotification[];

  @HasMany() streamsUsers: StreamsUser[];

  @Attr() tvContent: boolean;

  @Attr() tvTier: boolean;

  @Attr() type: number;

  @Attr({ persist: false }) updatedAt: string;

  @BelongsTo() user: User;

  @Attr() userId: number;

  @Attr({ persist: false }) usersCount: number;
}

export default Stream;
